import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import Iconify from 'src/components/iconify';

export default function InkfrogImportResults({ open, onClose, result }) {
  if (!result) return null;

  const {
    groupA,
    groupB,
    totalImported,
    rowErrors,
    stats,
    duplicatesSkipped,
  } = result;

  const hasErrors = rowErrors?.length > 0;
  const unmatchedActive = stats?.unmatchedActive || 0;
  const placeholderCount = stats?.placeholderIntegrationsCreated || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify icon="eva:checkmark-circle-2-fill" width={24} sx={{ color: 'success.main' }} />
        Import complete
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* Summary stats */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1.5,
            }}
          >
            <ResultStat
              label="Live on eBay"
              value={groupA?.imported || 0}
              icon="eva:cloud-upload-fill"
              color="success.main"
            />
            <ResultStat
              label="Imported as Drafts"
              value={groupB?.imported || 0}
              icon="eva:file-text-fill"
              color="info.main"
            />
            <ResultStat
              label="Total Imported"
              value={totalImported || 0}
              icon="eva:layers-fill"
              color="primary.main"
            />
            {groupA?.overLimit > 0 && (
              <ResultStat
                label="Over Plan Limit"
                value={groupA.overLimit}
                icon="eva:alert-triangle-fill"
                color="warning.main"
              />
            )}
            {duplicatesSkipped > 0 && (
              <ResultStat
                label="Skipped"
                value={duplicatesSkipped}
                icon="eva:copy-fill"
                color="warning.main"
              />
            )}
          </Box>

          {/* Over-limit warning */}
          {groupA?.overLimit > 0 && (
            <Alert severity="warning">
              {groupA.overLimit} listing{groupA.overLimit === 1 ? '' : 's'} live on eBay didn&apos;t fit
              in your plan, so {groupA.overLimit === 1 ? 'it came in as a draft' : 'they came in as drafts'}.
              {' '}Upgrade your plan to manage {groupA.overLimit === 1 ? 'it' : 'them'} as live listings.
            </Alert>
          )}

          {/* Duplicates skipped */}
          {duplicatesSkipped > 0 && (
            <Alert severity="info">
              {duplicatesSkipped} listing{duplicatesSkipped === 1 ? " wasn't" : "s weren't"} imported
              because you left {duplicatesSkipped === 1 ? 'it' : 'them'} out.
            </Alert>
          )}

          {/* No eBay integration */}
          {stats?.hasEbayIntegration === false && (
            <Alert severity="info">
              Your eBay account isn&apos;t connected, so every listing came in as a draft. Connect it on
              the Marketplaces page, then import from eBay to link the live ones.
            </Alert>
          )}

          {/* Placeholder integrations created */}
          {placeholderCount > 0 && (
            <Alert severity="info">
              {placeholderCount} eBay account{placeholderCount === 1 ? " in your file isn't" : "s in your file aren't"}
              {' '}connected yet. Connect {placeholderCount === 1 ? 'it' : 'them'} on the Marketplaces page so
              {' '}Voolist can keep {placeholderCount === 1 ? 'its' : 'their'} listings up to date.
            </Alert>
          )}

          {/* eBay fetch error */}
          {stats?.ebayFetchError && (
            <Alert severity="warning">
              Couldn&apos;t get your listings from eBay, so everything came in as drafts. To link the
              ones that are live on eBay, click Import Listings and pick your eBay account.
            </Alert>
          )}

          {/* Unmatched potentially active */}
          {unmatchedActive > 0 && (
            <Alert severity="info">
              {unmatchedActive} in-stock listing{unmatchedActive === 1 ? ' wasn’t' : 's weren’t'} found on your eBay
              account, so {unmatchedActive === 1 ? 'it came in as a draft' : 'they came in as drafts'}. If
              {' '}{unmatchedActive === 1 ? "it's" : "they're"} on eBay, click Import Listings and pick your eBay
              {' '}account to link {unmatchedActive === 1 ? 'it' : 'them'}.
            </Alert>
          )}

          {/* Row Errors */}
          {hasErrors && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="error.main">
                Listings that couldn&apos;t be imported ({rowErrors.length})
              </Typography>
              <Box
                sx={{
                  maxHeight: 150,
                  overflow: 'auto',
                  bgcolor: 'background.neutral',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                {rowErrors.slice(0, 20).map((err, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Row {err.row}: {err.error}
                  </Typography>
                ))}
                {rowErrors.length > 20 && (
                  <Typography variant="caption" color="text.secondary">
                    ...and {rowErrors.length - 20} more
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* Image transfer note */}
          <Alert severity="success" variant="outlined" icon={<Iconify icon="eva:image-fill" width={20} />}>
            Your photos are still copying over from inkFrog. With a lot of listings, this can take a
            while.
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

InkfrogImportResults.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  result: PropTypes.object,
};

function ResultStat({ label, value, icon, color = 'text.primary' }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'background.neutral',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Iconify icon={icon} width={20} sx={{ color }} />
      <Box>
        <Typography variant="subtitle2" sx={{ color }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Box>
    </Box>
  );
}

ResultStat.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  icon: PropTypes.string,
  color: PropTypes.string,
};
